import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { clampSize, useDragResize } from "../FarmMap/useDragResize";
import { buildCustomPeriod } from "./fieldAppPeriodUtils";
import FieldAppTrackingPanel from "./FieldAppTrackingPanel";

import "./FieldAppTelemetryMapPanel.css";

const PANEL_STORAGE_PREFIX = "field-app-telemetry-map-panel-";

const DEFAULT_LAYOUT = {
  x: 24,
  y: 48,
  width: 720,
  height: 520,
};

function readLayout(farmId) {
  try {
    const raw = localStorage.getItem(`${PANEL_STORAGE_PREFIX}${farmId}`);
    if (!raw) return DEFAULT_LAYOUT;
    const parsed = JSON.parse(raw);
    return {
      x: Number(parsed.x) || DEFAULT_LAYOUT.x,
      y: Number(parsed.y) || DEFAULT_LAYOUT.y,
      width: clampSize(Number(parsed.width) || DEFAULT_LAYOUT.width, 420, 1200),
      height: clampSize(Number(parsed.height) || DEFAULT_LAYOUT.height, 280, 900),
    };
  } catch (error) {
    return DEFAULT_LAYOUT;
  }
}

function writeLayout(farmId, layout) {
  try {
    localStorage.setItem(`${PANEL_STORAGE_PREFIX}${farmId}`, JSON.stringify(layout));
  } catch (error) {
    /* ignore */
  }
}

function sanitizeLayout(layout, bounds) {
  const width = clampSize(layout.width, 420, bounds ? bounds.width - 16 : 1200);
  const height = clampSize(layout.height, 280, bounds ? bounds.height - 16 : 900);
  const maxX = bounds ? Math.max(8, bounds.width - width - 8) : 9999;
  const maxY = bounds ? Math.max(8, bounds.height - height - 8) : 9999;
  return {
    x: clampSize(layout.x, 8, maxX),
    y: clampSize(layout.y, 8, maxY),
    width,
    height,
  };
}

function FieldAppTelemetryMapPanel({ farmId, selection, view, onClose, t }) {
  const [bounds, setBounds] = useState(() => ({
    width: typeof window !== "undefined" ? window.innerWidth : 1200,
    height: typeof window !== "undefined" ? window.innerHeight : 800,
  }));
  const [layout, setLayout] = useState(() => readLayout(farmId));
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    setLayout(readLayout(farmId));
  }, [farmId]);

  useEffect(() => {
    writeLayout(farmId, layout);
  }, [farmId, layout]);

  useEffect(() => {
    function updateBounds() {
      setBounds({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    updateBounds();
    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  useEffect(() => {
    if (!view) return;
    setRefreshToken((value) => value + 1);
  }, [
    view && view.token,
    view && view.dayKey,
    view && view.focusStart,
    view && view.focusEnd,
    view && view.sessionUuid,
  ]);

  const daySelection = useMemo(() => {
    if (!view || !view.dayKey) return null;
    const period = buildCustomPeriod(view.dayKey, view.dayKey);
    const operatorIds =
      view.operatorIds && view.operatorIds.length
        ? view.operatorIds
        : (selection && selection.operatorIds) || [];
    const equipmentIds =
      view.equipmentIds && view.equipmentIds.length
        ? view.equipmentIds
        : (selection && selection.equipmentIds) || [];
    const machineIdents =
      view.machineIdents && view.machineIdents.length
        ? view.machineIdents
        : (selection && selection.machineIdents) || [];
    return {
      ...(selection || {}),
      operatorIds,
      equipmentIds,
      machineIdents,
      allMachines:
        equipmentIds.length || machineIdents.length
          ? false
          : Boolean(selection && selection.allMachines),
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      periodAutoToday: false,
      periodPreset: "custom",
    };
  }, [selection, view]);

  const focusGraphPeriod = useMemo(() => {
    if (!view || !view.focusStart || !view.focusEnd) return null;
    return {
      startIso: view.focusStart,
      endIso: view.focusEnd,
      token: view.token || Date.now(),
      silent: Boolean(view.silentFocus),
    };
  }, [view]);

  const handlePanelResize = useCallback(
    (axis, dx, dy) => {
      if (axis === "move") {
        setLayout((current) =>
          sanitizeLayout(
            {
              ...current,
              x: current.x + dx,
              y: current.y + dy,
            },
            bounds
          )
        );
        return;
      }
      if (axis === "resize") {
        setLayout((current) =>
          sanitizeLayout(
            {
              ...current,
              width: current.width + dx,
              height: current.height + dy,
            },
            bounds
          )
        );
      }
    },
    [bounds]
  );

  const { onResizeStart } = useDragResize(handlePanelResize);

  const handleClose = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!view) return undefined;

    function onKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [view, onClose]);

  if (!view || !daySelection || typeof document === "undefined") {
    return null;
  }

  const title = view.label
    ? `${view.label} — ${view.dayKey}`
    : t("Rastreamento do trecho");

  return createPortal(
    <div className="field-app-telemetry-map-shell">
      <div
        className="field-app-telemetry-map-panel"
        style={{
          left: layout.x,
          top: layout.y,
          width: layout.width,
          height: layout.height,
        }}
      >
        <div className="field-app-telemetry-map-panel__header">
          <div
            className="field-app-telemetry-map-panel__drag"
            onMouseDown={(event) => onResizeStart("move", event)}
          >
            <span>{title}</span>
          </div>
          <button
            type="button"
            className="field-app-telemetry-map-panel__close"
            aria-label={t("Fechar mapa")}
            title={t("Fechar mapa")}
            onMouseDown={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={handleClose}
          >
            ×
          </button>
        </div>
        <div className="field-app-telemetry-map-panel__body">
          <FieldAppTrackingPanel
            embedded
            farmId={farmId}
            selection={daySelection}
            chartSelection={daySelection}
            selectedDayKeys={[view.dayKey]}
            periodDayKeys={[view.dayKey]}
            active
            refreshToken={refreshToken}
            focusGraphPeriod={focusGraphPeriod}
            onFocusGraphPeriodHandled={() => {}}
            focusSessionUuid={view.sessionUuid || null}
            onFocusSessionHandled={() => {}}
            initialMapCenter={view.mapCenter || null}
            mapFullDayPath={Boolean(view.mapFullDay)}
            highlightMapPoint={view.mapCenter || null}
            t={t}
          />
        </div>
        <div
          className="field-app-telemetry-map-panel__resize"
          title={t("Arraste para redimensionar")}
          onMouseDown={(event) => onResizeStart("resize", event)}
        />
      </div>
    </div>,
    document.body
  );
}

export default FieldAppTelemetryMapPanel;
